// DealTable — the deal-level detail table AA's "HubSpot Deals | MoM" report
// page ends on (reference screenshot: "Showing 50 of 289 Rows", a search
// box, and a SOURCE-filtered second widget for the paid-search-to-revenue
// view). DealPipelinePanel already owns the funnel/KPI story above this;
// this frame is the raw row list underneath it.
//
// One filterable table gives us both AA screenshots for free: the plain
// "HubSpot Deals" table, and the "Google Ad Deals" table filtered to
// SOURCE = PAID_SEARCH — rather than shipping two separate widgets, a client-
// side source filter (chips) on the same loaded rows covers both.
//
// Same self-contained convention as the other frames in this directory
// (recharts/theme-tokens/type-import only) — see DealPipelinePanel.tsx's
// module docstring — with ONE shared helper: primitives.formatMoney (single
// source of truth for money formatting, 2026-08-10; this file's own local
// `formatAmount`, which always printed 2 decimals so a whole-dollar deal
// read "$0.00", is deleted, not just unused).

import { useMemo, useState } from "react";
import { formatLocalDate, formatMoney } from "@/components/client-analytics/primitives";
import type { DealRow } from "@/types/clientAnalytics";

export interface DealTableProps {
  rows: DealRow[];
  /** CrmSection.total_deals, when known — drives the "Showing N of M"
   * caption's denominator so the raw-pull cap reads as a sample, not the
   * whole account. Falls back to rows.length when omitted or smaller. */
  totalDeals?: number | null;
  currency?: string;
}

// Sample data for standalone preview / storybook-style rendering. Mirrors
// the AA reference screenshots' shape (music-licensing deals, PAID_SEARCH
// source, all currently OPEN).
export const demoDeals: DealRow[] = [
  {
    deal_id: "44821901",
    name: "Versant - Rainmaker S2 TRLR Dramatic Op",
    type: "Music Licence",
    created_at: "2026-07-28",
    closed_at: "2027-01-28",
    associated_contacts: 1,
    amount: 0,
    source: "PAID_SEARCH",
    deal_stage: "Blanket Search",
    is_closed: false,
  },
  {
    deal_id: "44819044",
    name: "Netflix - Poser - sexy thriller",
    type: "Music Licence",
    created_at: "2026-07-27",
    closed_at: "2027-01-27",
    associated_contacts: 1,
    amount: 0,
    source: "PAID_SEARCH",
    deal_stage: "Blanket Search",
    is_closed: false,
  },
  {
    deal_id: "44780219",
    name: "Nevatars",
    type: "Music Licence",
    created_at: "2026-07-20",
    closed_at: "2027-01-20",
    associated_contacts: 2,
    amount: 4000,
    source: "PAID_SEARCH",
    deal_stage: "Music Search",
    is_closed: false,
  },
  {
    deal_id: "44650312",
    name: "USA - Rainmaker S2 - Trailerized Timeless",
    type: "Music Licence",
    created_at: "2026-07-16",
    closed_at: "2027-01-16",
    associated_contacts: 1,
    amount: 0,
    source: "organic",
    deal_stage: "Blanket Search",
    is_closed: false,
  },
  {
    deal_id: "44120887",
    name: "Fracture Creative - Netflix Sizzle Reel",
    type: "Music Licence",
    created_at: "2026-06-22",
    closed_at: null,
    associated_contacts: 1,
    amount: 1500,
    source: "referral",
    deal_stage: "Closed-Won",
    is_closed: true,
  },
];

// "2026-07-28" (or a full ISO timestamp) -> "Jul 28, 2026". Matches
// DealPipelinePanel's formatDateRange month/day/year style. Parsing goes
// through the shared primitives.parseLocalDate: `new Date("YYYY-MM-DD")` is
// UTC midnight per the ES spec, so it rendered created_at and closed_at a day
// early in every US timezone.
export function formatDealDate(raw?: string | null): string {
  if (!raw) return "—";
  return formatLocalDate(raw) ?? raw;
}

// Whole dollars by default, matching every KPI/aggregate money value in
// this dashboard -- but this table renders ONE deal's amount per row (an
// individual record, not an aggregate), so a genuine cents component
// (e.g. a $53.40 deal) is real information a blanket 0-decimal round would
// silently erase. Never the reverse: a whole-dollar deal never prints the
// fabricated-precision "$4,000.00" the old always-2-decimals formatAmount
// used to.
function formatDealAmount(n: number, currency = "USD"): string {
  const hasCents = Math.round(n * 100) % 100 !== 0;
  return formatMoney(n, currency, hasCents ? 2 : 0);
}

function StatusPill({ isClosed }: { isClosed: boolean }) {
  const tone = isClosed
    ? "bg-muted text-muted-foreground"
    : "bg-[hsl(var(--status-success-bg))] text-[hsl(var(--status-success))]";
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide leading-none ${tone}`}>
      {isClosed ? "Closed" : "Open"}
    </span>
  );
}

export function DealTable({ rows, totalDeals, currency = "USD" }: DealTableProps) {
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState<string | null>(null);

  const sources = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) {
      if (r.source) set.add(r.source);
    }
    return Array.from(set).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (sourceFilter && r.source !== sourceFilter) return false;
      if (!q) return true;
      return (
        r.name.toLowerCase().includes(q) ||
        (r.deal_stage ?? "").toLowerCase().includes(q) ||
        (r.source ?? "").toLowerCase().includes(q) ||
        (r.type ?? "").toLowerCase().includes(q)
      );
    });
  }, [rows, search, sourceFilter]);

  // Adaptive: this frame renders nothing when there's no deal-level detail —
  // callers should still render the pipeline/funnel panel on its own (e.g.
  // Precision Restorations has zero CRM deals).
  if (!rows || rows.length === 0) return null;

  const shown = filtered.length;
  const total = totalDeals && totalDeals > rows.length ? totalDeals : rows.length;
  // Once a filter narrows the visible set below the loaded rows, the honest
  // denominator is the loaded-row count (what the filter is actually
  // slicing), not the unfiltered account total — matches AA's own "Showing
  // 11 of 11 Rows" once its widget is configured with a SOURCE filter.
  const denominator = search || sourceFilter ? rows.length : total;

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2 border-b border-border bg-muted/40">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
            Deals
          </span>
          {sources.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <button
                type="button"
                onClick={() => setSourceFilter(null)}
                className={`rounded-full px-2.5 py-0.5 text-dash-context font-semibold whitespace-nowrap ${
                  sourceFilter === null
                    ? "bg-coral text-white"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                All sources
              </button>
              {sources.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSourceFilter(sourceFilter === s ? null : s)}
                  className={`rounded-full px-2.5 py-0.5 text-dash-context font-semibold whitespace-nowrap ${
                    sourceFilter === s
                      ? "bg-coral text-white"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                  title={s}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search deals…"
            className="rounded-md border border-border bg-background px-2.5 py-1 text-dash-context text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          />
          <div className="text-dash-context text-muted-foreground tabular whitespace-nowrap">
            Showing {shown.toLocaleString()} of {denominator.toLocaleString()}
          </div>
        </div>
      </div>
      <div className="max-h-[420px] overflow-y-auto">
        <table className="w-full border-collapse text-dash-subtitle">
          <thead className="sticky top-0 z-10 bg-muted">
            <tr className="border-b border-border">
              <th className="px-4 py-2.5 text-left text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
                Deal name
              </th>
              <th className="px-4 py-2.5 text-left text-dash-label font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap">
                Date added
              </th>
              <th className="px-4 py-2.5 text-left text-dash-label font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap">
                Date closed
              </th>
              <th className="px-4 py-2.5 text-left text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
                Type
              </th>
              <th className="px-4 py-2.5 text-left text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
                Source
              </th>
              <th className="px-4 py-2.5 text-right text-dash-label font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap">
                Contacts
              </th>
              <th className="px-4 py-2.5 text-right text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
                Amount
              </th>
              <th className="px-4 py-2.5 text-center text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
                Status
              </th>
              <th className="px-4 py-2.5 text-left text-dash-label font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap">
                Deal stage
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row, i) => (
              <tr
                key={row.deal_id || `${row.name}-${i}`}
                className={`border-b border-border last:border-0 transition-colors hover:bg-muted/60 ${
                  i % 2 === 1 ? "bg-muted/30" : "bg-card"
                }`}
              >
                <td className="px-4 py-2 max-w-[260px] truncate text-foreground" title={row.name}>
                  {row.name}
                </td>
                <td className="px-4 py-2 whitespace-nowrap tabular text-muted-foreground">
                  {formatDealDate(row.created_at)}
                </td>
                <td className="px-4 py-2 whitespace-nowrap tabular text-muted-foreground">
                  {formatDealDate(row.closed_at)}
                </td>
                <td className="px-4 py-2 max-w-[140px] truncate text-muted-foreground" title={row.type ?? undefined}>
                  {row.type ?? "—"}
                </td>
                <td className="px-4 py-2 max-w-[140px] truncate text-muted-foreground" title={row.source ?? undefined}>
                  {row.source ?? "—"}
                </td>
                <td className="px-4 py-2 text-right tabular text-muted-foreground">
                  {row.associated_contacts ?? "—"}
                </td>
                <td className="px-4 py-2 text-right tabular text-foreground whitespace-nowrap">
                  {formatDealAmount(row.amount, currency)}
                </td>
                <td className="px-4 py-2 text-center">
                  <StatusPill isClosed={row.is_closed} />
                </td>
                <td className="px-4 py-2 max-w-[160px] truncate text-muted-foreground" title={row.deal_stage}>
                  {row.deal_stage}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-6 text-center text-dash-context text-muted-foreground">
                  No deals match this filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
